import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FraudQueue } from "@/components/fraud/FraudQueue";
import { RulesConfig } from "@/components/fraud/RulesConfig";
import { Proposals } from "@/components/fraud/Proposals";
import { FraudSimulator } from "@/components/fraud/FraudSimulator";
import { FraudLearning } from "@/components/fraud/FraudLearning";

const TABS = [["queue", "Live queue"], ["rules", "Rules & thresholds"], ["versions", "Approvals & versions"], ["simulator", "Simulator & test API"], ["learning", "AI learning & metrics"]];

export default function FraudEngine() {
  const [tab, setTab] = useState("queue");
  const [cfg, setCfg] = useState(null);
  const loadCfg = useCallback(() => api.get("/fraud/config").then((r) => setCfg(r.data)), []);
  useEffect(() => { loadCfg(); }, [loadCfg]);
  const a = cfg?.active;
  return (
    <div className="flex-1 min-w-0 overflow-y-auto thin-scroll grid-bg" data-testid="fraud-engine-page">
      <div className="px-5 sm:px-8 py-8 space-y-5 max-w-[1600px]">
        <div className="rise">
          <div className="chip border-rose-200 bg-rose-50 text-rose-700 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" />Fraud engine</div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight hero-title pb-1">Before money leaves the bank</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">Configurable rules score every SWIFT, ACH, wire, RTP and stablecoin payment into Green, Yellow or Red. Sanctions are a separate legal control that the score never overrides. The engine learns from confirmed outcomes and re-tunes rule weights automatically; people propose and approve every other change, with a shadow replay first.</p>
        </div>
        {a && (
          <div className="flex flex-wrap gap-2 text-[11px]" data-testid="fraud-active-summary">
            <span className="chip bg-white border-slate-200">Active <b className="ml-1" data-testid="fraud-active-version">{a.version}</b></span>
            <span className="chip bg-white border-slate-200" data-testid="fraud-active-mode">Mode: <b className="ml-1">{a.mode}</b></span>
            <span className="chip bg-emerald-50 border-emerald-200 text-emerald-700">Green &lt; {a.thresholds.yellow}</span>
            <span className="chip bg-amber-50 border-amber-200 text-amber-800">Yellow {a.thresholds.yellow}-{a.thresholds.red - 1}</span>
            <span className="chip bg-red-50 border-red-200 text-red-700">Red ≥ {a.thresholds.red}</span>
            {cfg.proposed.length > 0 && <button onClick={() => setTab("versions")} className="chip bg-sky-50 border-sky-200 text-sky-700" data-testid="fraud-pending-chip">{cfg.proposed.length} proposal(s) awaiting approval</button>}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="fraud-tabs">
          {TABS.map(([k, l]) => <button key={k} data-testid={`fraud-tab-${k}`} onClick={() => setTab(k)} className={`text-xs font-semibold px-3.5 py-2 rounded-xl border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>)}
        </div>
        {!cfg ? <div className="text-sm text-slate-500">Loading…</div> : <>
          {tab === "queue" && <FraudQueue onCfg={loadCfg} />}
          {tab === "rules" && <RulesConfig cfg={cfg} onDone={async () => { await loadCfg(); setTab("versions"); }} />}
          {tab === "versions" && <Proposals cfg={cfg} setCfg={setCfg} />}
          {tab === "simulator" && <FraudSimulator cfg={cfg} />}
          {tab === "learning" && <FraudLearning />}
        </>}
      </div>
    </div>
  );
}
